import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowLeft, BarChart3, Building2, Globe, Users, CheckCircle2,
  TrendingUp, AlertTriangle, Award, ChevronRight
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const INDUSTRIES = [
  { key: 'manufacturing', label: 'Manufacturing' },
  { key: 'retail', label: 'Retail & Consumer Goods' },
  { key: 'automotive', label: 'Automotive' },
  { key: 'healthcare', label: 'Healthcare & Life Sciences' },
  { key: 'logistics', label: 'Logistics & Supply Chain' },
  { key: 'energy', label: 'Energy & Utilities' },
  { key: 'finance', label: 'Financial Services' },
  { key: 'food', label: 'Food & Beverages' },
  { key: 'travel', label: 'Travel & Technology' },
  { key: 'construction', label: 'Construction' },
  { key: 'public-sector', label: 'Public Sector' },
  { key: 'other', label: 'Other' },
];

const MATURITY_LEVELS = [
  { level: 1, label: 'Manual', color: '#ef4444', desc: 'Spreadsheets, email-based processes, manual portal submissions' },
  { level: 2, label: 'Semi-Automated', color: '#f59e0b', desc: 'Some automation but fragmented tools, multiple vendors' },
  { level: 3, label: 'Automated', color: '#22c55e', desc: 'Unified platform, automated submissions, proactive monitoring' },
  { level: 4, label: 'Optimised', color: '#0066CC', desc: 'Full automation, real-time analytics, continuous compliance improvement' },
];

const QUESTIONS = [
  {
    id: 'countries',
    question: 'How many countries do you file tax compliance in?',
    type: 'select',
    options: [
      { value: '1-5', label: '1–5 countries', score: 1 },
      { value: '6-15', label: '6–15 countries', score: 2 },
      { value: '16-30', label: '16–30 countries', score: 3 },
      { value: '30+', label: '30+ countries', score: 4 },
    ],
  },
  {
    id: 'invoices',
    question: 'Approximately how many invoices do you process per month?',
    type: 'select',
    options: [
      { value: '<10k', label: 'Under 10,000', score: 1 },
      { value: '10k-100k', label: '10,000 – 100,000', score: 2 },
      { value: '100k-1m', label: '100,000 – 1 million', score: 3 },
      { value: '1m+', label: 'Over 1 million', score: 4 },
    ],
  },
  {
    id: 'vendors',
    question: 'How many different vendors/tools do you use for tax compliance?',
    type: 'select',
    options: [
      { value: '1', label: '1 vendor (unified)', score: 4 },
      { value: '2-3', label: '2–3 vendors', score: 3 },
      { value: '4-6', label: '4–6 vendors', score: 2 },
      { value: '7+', label: '7+ vendors or mostly manual', score: 1 },
    ],
  },
  {
    id: 'team',
    question: 'How many FTEs are dedicated to tax compliance operations?',
    type: 'select',
    options: [
      { value: '1-3', label: '1–3 people', score: 2 },
      { value: '4-10', label: '4–10 people', score: 3 },
      { value: '11-25', label: '11–25 people', score: 3 },
      { value: '25+', label: '25+ people', score: 4 },
    ],
  },
  {
    id: 'outsource',
    question: 'What percentage of your compliance work is outsourced to external firms?',
    type: 'select',
    options: [
      { value: '0-10%', label: '0–10% (mostly in-house)', score: 4 },
      { value: '10-40%', label: '10–40%', score: 3 },
      { value: '40-70%', label: '40–70%', score: 2 },
      { value: '70%+', label: '70%+ (mostly outsourced)', score: 1 },
    ],
  },
  {
    id: 'erp',
    question: 'Is your compliance integrated with your ERP system?',
    type: 'select',
    options: [
      { value: 'native', label: 'Yes, natively embedded', score: 4 },
      { value: 'api', label: 'Yes, via API integration', score: 3 },
      { value: 'batch', label: 'Partially — batch file uploads', score: 2 },
      { value: 'none', label: 'No — separate systems entirely', score: 1 },
    ],
  },
];

/* Maturity bands — relative positioning, no fabricated external data */
const getBand = (score) => {
  if (score <= 8) return { level: 1, percentile: 'bottom 25%', insight: 'Most organisations at this maturity level are spending significantly more on compliance operations than necessary and carry elevated audit risk.' };
  if (score <= 14) return { level: 2, percentile: 'middle 50%', insight: 'You have some automation in place, but fragmented tooling and vendor sprawl are likely driving up costs and creating blind spots.' };
  if (score <= 19) return { level: 3, percentile: 'top 25%', insight: 'Your compliance operations are well-structured. The primary opportunities lie in further consolidation and real-time monitoring capabilities.' };
  return { level: 4, percentile: 'top 10%', insight: 'Your compliance maturity is advanced. Continuous improvement through AI-driven analytics and predictive compliance would be the next frontier.' };
};

const getRecommendations = (answers) => {
  const recs = [];
  if (answers.vendors && ['4-6', '7+'].includes(answers.vendors)) {
    recs.push({ title: 'Consolidate your vendor landscape', desc: 'Organisations using 4+ compliance vendors typically face higher integration costs, data inconsistency, and slower incident response. A single-platform approach can reduce total cost of ownership significantly.', priority: 'high' });
  }
  if (answers.outsource && ['40-70%', '70%+'].includes(answers.outsource)) {
    recs.push({ title: 'Evaluate insourcing potential', desc: 'A high outsourcing ratio for rules-based, repeatable work (VAT returns, e-Invoicing) suggests an opportunity to bring operations in-house with the right technology — reducing recurring costs while building internal capability.', priority: 'high' });
  }
  if (answers.erp && ['batch', 'none'].includes(answers.erp)) {
    recs.push({ title: 'Embed compliance in your ERP', desc: 'Operating compliance separately from your ERP creates data re-entry, reconciliation gaps, and delayed submissions. Native or middleware-based ERP integration eliminates these issues.', priority: 'medium' });
  }
  if (answers.countries && ['16-30', '30+'].includes(answers.countries)) {
    recs.push({ title: 'Adopt a multi-country platform strategy', desc: 'Managing 16+ country mandates with fragmented tools creates exponential complexity. A unified platform with pre-built country mandates simplifies rollout and reduces per-country compliance costs.', priority: 'medium' });
  }
  if (recs.length === 0) {
    recs.push({ title: 'Optimise with real-time analytics', desc: 'Your operations are well-structured. The next step is leveraging real-time compliance analytics to identify trends, predict issues, and continuously improve submission success rates.', priority: 'low' });
  }
  return recs;
};

const BenchmarksPage = () => {
  const [step, setStep] = useState('industry'); // 'industry' | 'questions' | 'results' | 'lead'
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [leadData, setLeadData] = useState({ name: '', email: '', company: '' });
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  const totalScore = Object.values(answers).reduce((sum, v) => sum + (v?.score || 0), 0);
  const maxScore = QUESTIONS.length * 4;
  const band = getBand(totalScore);
  const maturityLevel = MATURITY_LEVELS[band.level - 1];
  const recommendations = getRecommendations(
    Object.fromEntries(Object.entries(answers).map(([k, v]) => [k, v?.value]))
  );

  const handleAnswer = (questionId, option) => {
    setAnswers(prev => ({ ...prev, [questionId]: option }));
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${API}/api/leads/benchmark`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...leadData,
          industry: selectedIndustry,
          answers,
          maturity_level: band.level,
          total_score: totalScore,
        }),
      });
    } catch {}
    setLeadSubmitted(true);
  };

  return (
    <PageLayout>
      {/* Hero */}
      <section className="bg-slate-900 pt-8 pb-12" data-testid="benchmarks-hero">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/10 text-amber-400 text-sm font-medium rounded-full mb-6">
            <BarChart3 className="w-4 h-4" strokeWidth={1.5} />
            Compliance Maturity Assessment
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4" data-testid="benchmarks-title">
            Where does your compliance maturity{' '}
            <span className="text-amber-400">really sit?</span>
          </h1>
          <p className="text-base text-slate-400 max-w-2xl mx-auto">
            Answer 6 quick questions about your compliance operations. We'll show you where you sit on the maturity scale and where the biggest opportunities for improvement lie.
          </p>
        </div>
      </section>

      {/* Progress bar */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-6 py-3">
          <div className="flex items-center gap-4">
            {['Industry', 'Assessment', 'Results'].map((label, i) => {
              const stepIndex = i === 0 ? 'industry' : i === 1 ? 'questions' : 'results';
              const isActive = step === stepIndex || (step === 'lead' && i === 2);
              const isDone = (i === 0 && step !== 'industry') || (i === 1 && (step === 'results' || step === 'lead'));
              return (
                <div key={label} className="flex items-center gap-2 flex-1">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${isDone ? 'bg-taxera-teal text-white' : isActive ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-400'}`}>
                    {isDone ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  <span className={`text-xs font-medium ${isActive || isDone ? 'text-taxera-charcoal' : 'text-slate-400'}`}>{label}</span>
                  {i < 2 && <div className={`flex-1 h-px ${isDone ? 'bg-taxera-teal' : 'bg-slate-200'}`} />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="py-12 lg:py-16 bg-white" data-testid="benchmarks-content">
        <div className="max-w-3xl mx-auto px-6">
          <AnimatePresence mode="wait">
            {/* Step 1: Industry Selection */}
            {step === 'industry' && (
              <motion.div key="industry" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <h2 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">Select your industry</h2>
                <p className="text-sm text-slate-500 mb-8">This helps us contextualise your results within your sector.</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {INDUSTRIES.map((ind) => (
                    <button
                      key={ind.key}
                      onClick={() => setSelectedIndustry(ind.key)}
                      className={`px-4 py-3 rounded-xl border text-sm font-medium text-left transition-all ${
                        selectedIndustry === ind.key
                          ? 'border-amber-400 bg-amber-50 text-amber-700'
                          : 'border-slate-200 text-slate-600 hover:border-amber-200 hover:bg-amber-50/30'
                      }`}
                      data-testid={`industry-${ind.key}`}
                    >
                      {ind.label}
                    </button>
                  ))}
                </div>
                <div className="mt-8 flex justify-end">
                  <button
                    onClick={() => selectedIndustry && setStep('questions')}
                    disabled={!selectedIndustry}
                    className="group inline-flex items-center gap-2 px-8 py-3 taxera-gradient text-white rounded-full font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg transition-all"
                    data-testid="industry-next"
                  >
                    Continue <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Questions */}
            {step === 'questions' && (
              <motion.div key="questions" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <div className="flex items-center justify-between mb-2">
                  <h2 className="font-heading text-xl font-bold text-taxera-charcoal">
                    Question {currentQ + 1} of {QUESTIONS.length}
                  </h2>
                  <span className="text-xs text-slate-400 font-medium">
                    {Math.round(((currentQ + 1) / QUESTIONS.length) * 100)}% complete
                  </span>
                </div>
                <div className="h-1 bg-slate-100 rounded-full mb-8">
                  <div className="h-1 bg-amber-500 rounded-full transition-all" style={{ width: `${((currentQ + 1) / QUESTIONS.length) * 100}%` }} />
                </div>

                <AnimatePresence mode="wait">
                  <motion.div key={currentQ} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                    <p className="text-base text-taxera-charcoal font-medium mb-6">{QUESTIONS[currentQ].question}</p>
                    <div className="space-y-3">
                      {QUESTIONS[currentQ].options.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => handleAnswer(QUESTIONS[currentQ].id, opt)}
                          className={`w-full text-left px-5 py-4 rounded-xl border text-sm font-medium transition-all ${
                            answers[QUESTIONS[currentQ].id]?.value === opt.value
                              ? 'border-amber-400 bg-amber-50 text-amber-700'
                              : 'border-slate-200 text-slate-600 hover:border-amber-200'
                          }`}
                          data-testid={`option-${opt.value}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </AnimatePresence>

                <div className="mt-8 flex items-center justify-between">
                  <button
                    onClick={() => currentQ > 0 ? setCurrentQ(currentQ - 1) : setStep('industry')}
                    className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  <button
                    onClick={() => {
                      if (currentQ < QUESTIONS.length - 1) setCurrentQ(currentQ + 1);
                      else setStep('results');
                    }}
                    disabled={!answers[QUESTIONS[currentQ].id]}
                    className="group inline-flex items-center gap-2 px-8 py-3 taxera-gradient text-white rounded-full font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg transition-all"
                    data-testid="question-next"
                  >
                    {currentQ < QUESTIONS.length - 1 ? 'Next' : 'See Results'}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Results */}
            {(step === 'results' || step === 'lead') && (
              <motion.div key="results" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                {/* Maturity Score */}
                <div className="text-center mb-10">
                  <h2 className="font-heading text-2xl font-bold text-taxera-charcoal mb-4">Your Compliance Maturity</h2>
                  <div className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl border-2" style={{ borderColor: maturityLevel.color, backgroundColor: `${maturityLevel.color}08` }}>
                    <div className="font-heading text-4xl font-bold" style={{ color: maturityLevel.color }}>
                      {totalScore}/{maxScore}
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-bold" style={{ color: maturityLevel.color }}>Level {band.level}: {maturityLevel.label}</div>
                      <div className="text-xs text-slate-500">Estimated {band.percentile} of comparable organisations</div>
                    </div>
                  </div>
                </div>

                {/* Maturity Scale */}
                <div className="mb-10">
                  <div className="flex gap-1 mb-3">
                    {MATURITY_LEVELS.map((ml) => (
                      <div
                        key={ml.level}
                        className={`flex-1 h-3 rounded-full transition-all ${band.level >= ml.level ? '' : 'opacity-20'}`}
                        style={{ backgroundColor: ml.color }}
                      />
                    ))}
                  </div>
                  <div className="flex justify-between">
                    {MATURITY_LEVELS.map((ml) => (
                      <div key={ml.level} className={`text-2xs font-medium ${band.level === ml.level ? 'text-taxera-charcoal font-bold' : 'text-slate-400'}`}>
                        {ml.label}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Insight */}
                <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 mb-8">
                  <h3 className="text-sm font-bold text-taxera-charcoal mb-2">What this means</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{band.insight}</p>
                </div>

                {/* Recommendations */}
                <div className="mb-8">
                  <h3 className="font-heading text-lg font-bold text-taxera-charcoal mb-4">Recommendations</h3>
                  <div className="space-y-4">
                    {recommendations.map((rec, i) => (
                      <div key={i} className="rounded-xl border border-slate-200 p-5" style={{ borderLeftWidth: 3, borderLeftColor: rec.priority === 'high' ? '#ef4444' : rec.priority === 'medium' ? '#f59e0b' : '#22c55e' }}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-2xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                            rec.priority === 'high' ? 'bg-red-50 text-red-500' : rec.priority === 'medium' ? 'bg-amber-50 text-amber-600' : 'bg-green-50 text-green-600'
                          }`}>
                            {rec.priority} priority
                          </span>
                        </div>
                        <h4 className="text-sm font-bold text-taxera-charcoal mb-1">{rec.title}</h4>
                        <p className="text-xs text-slate-500 leading-relaxed">{rec.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Lead capture or CTAs */}
                {!leadSubmitted ? (
                  <div className="bg-slate-900 rounded-2xl p-8">
                    <h3 className="font-heading text-lg font-bold text-white mb-2">Get your detailed report</h3>
                    <p className="text-sm text-slate-400 mb-6">
                      Receive a personalised PDF with your full maturity assessment, peer comparison insights, and a prioritised improvement roadmap.
                    </p>
                    <form onSubmit={handleLeadSubmit} className="grid md:grid-cols-3 gap-3">
                      <input
                        type="text"
                        placeholder="Your name"
                        value={leadData.name}
                        onChange={(e) => setLeadData(p => ({ ...p, name: e.target.value }))}
                        className="px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-amber-400"
                        required
                        data-testid="lead-name"
                      />
                      <input
                        type="email"
                        placeholder="Work email"
                        value={leadData.email}
                        onChange={(e) => setLeadData(p => ({ ...p, email: e.target.value }))}
                        className="px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-amber-400"
                        required
                        data-testid="lead-email"
                      />
                      <button
                        type="submit"
                        className="px-6 py-3 bg-amber-500 text-white rounded-xl font-semibold text-sm hover:bg-amber-400 transition-colors"
                        data-testid="lead-submit"
                      >
                        Send my report
                      </button>
                    </form>
                  </div>
                ) : (
                  <div className="bg-taxera-teal/5 border border-taxera-teal/20 rounded-2xl p-8 text-center">
                    <CheckCircle2 className="w-10 h-10 text-taxera-teal mx-auto mb-3" />
                    <h3 className="font-heading text-lg font-bold text-taxera-charcoal mb-2">Report requested</h3>
                    <p className="text-sm text-slate-500 mb-6">We'll send your personalised maturity report to {leadData.email} shortly.</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Link to="/evaluate" className="inline-flex items-center gap-2 px-6 py-3 taxera-gradient text-white rounded-full font-semibold text-sm hover:shadow-lg transition-all">
                        Take the Diagnostic <ArrowRight className="w-4 h-4" />
                      </Link>
                      <Link to="/evaluate/business-case" className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 text-taxera-charcoal rounded-full font-semibold text-sm hover:bg-slate-50 transition-all">
                        Build Your Business Case <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                )}

                {/* Disclaimer */}
                <p className="text-2xs text-slate-400 text-center mt-6 leading-relaxed">
                  This assessment provides a directional maturity indication based on your self-reported inputs. Percentile estimates reflect general industry patterns observed by Taxera and are not derived from a specific external benchmark study. For a detailed, data-driven compliance audit, please <Link to="/contact" className="text-taxera-teal hover:underline">contact our team</Link>.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </PageLayout>
  );
};

export default BenchmarksPage;
